import React, { useState } from "react";
import { api } from "../lib/api";
import { Loader2 } from "lucide-react";

const ROLES = [
  { key: "data_steward", label: "Data Steward", actions: ["confirm", "refuse", "escalate"] },
  { key: "policy_owner", label: "Policy Owner", actions: ["confirm", "refuse", "escalate"] },
  { key: "assurance_reviewer", label: "AI Assurance Reviewer", actions: ["sign_off", "refuse", "escalate"] },
];

function actionLabel(a) {
  if (a === "confirm") return "Confirm";
  if (a === "sign_off") return "Sign off";
  if (a === "refuse") return "Refuse";
  if (a === "escalate") return "Escalate";
  return a;
}

export default function ReviewActions({ request, onChange }) {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  async function submit(role, action) {
    setBusy(`${role}:${action}`);
    setError(null);
    try {
      const actor = ROLES.find((r) => r.key === role)?.label || role;
      const note =
        action === "refuse"
          ? "Reviewer refused under current evidence boundary."
          : action === "escalate"
          ? "Reviewer escalated for additional authority."
          : "Reviewer confirmed within declared purpose and policy.";
      await api.submitReview(request.id, { role, actor, action, note });
      onChange && (await onChange());
    } catch (e) {
      setError(e.message || "Review failed.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="border border-slate-200 bg-white p-7" data-testid="review-actions">
      <span className="eyebrow">Authority review</span>
      <h3 className="font-display text-2xl text-[var(--ink-heading)] mt-1 mb-5">
        Submit a governed review action
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {ROLES.map((r) => (
          <div key={r.key} className="border border-slate-200 p-5 flex flex-col gap-3">
            <p className="font-display text-lg text-[var(--ink-heading)]">{r.label}</p>
            <div className="flex flex-wrap gap-2">
              {r.actions.map((a) => (
                <button
                  key={a}
                  data-testid={`review-${r.key}-${a}`}
                  onClick={() => submit(r.key, a)}
                  disabled={!!busy}
                  className="px-3 py-1.5 border border-slate-300 text-xs uppercase tracking-[0.14em] hover:bg-slate-900 hover:text-white transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {busy === `${r.key}:${a}` && <Loader2 size={12} className="animate-spin" />}
                  {actionLabel(a)}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {error && (
        <p className="text-xs text-red-700 mt-4" data-testid="review-error">{error}</p>
      )}
      <p className="text-xs text-[var(--ink-muted)] mt-5">
        Each review action records a KATLAS receipt request. The signed
        envelope is held server-side; only whitelisted fields are surfaced.
      </p>
    </section>
  );
}
